export type Role = 'admin' | 'member';

export type FieldType = 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'checkbox';

export type Entity = 'companies' | 'people' | 'deals' | 'activities' | 'issues';

export interface CustomFieldDef {
  id: number;
  vertical_id: number;
  entity: Entity;
  key: string;
  label: string;
  type: FieldType;
  options: string[];
  sort_order: number;
}

export interface Vertical {
  id: number;
  name: string;
  slug: string;
  settings: { deal_stages?: string[]; color?: string; stage_colors?: Record<string, string>; category_colors?: Record<string, string>; [key: string]: unknown } | null;
}

export interface Me {
  happy_code_queue_access?: boolean;
  issue_verticals?: Vertical[];
  user: {
    id: number;
    email: string;
    name: string;
    role: Role;
    vertical_id: number | null;
    totp_enabled: boolean;
    issues_only: boolean;
    dispatch_only?: boolean;
    intake_coordinator?: boolean;
    email_access: boolean;
  };
  verticals: Vertical[];
  custom_fields: Record<Entity, CustomFieldDef[]>;
  // Whether "Send to dev" (POST /api/issues/:id/send-to-dev) is configured
  // server-side (both GENBA_URL and GENBA_TOKEN set) — drives the action's
  // visibility in IssueDetailPanel.
  genba_enabled: boolean;
}

export interface Paginated<T> {
  results: T[];
  count: number;
  page: number;
  per_page: number;
}

export type CustomValues = Record<string, unknown>;

export type PartnerStatus = 'prospect' | 'in_discussion' | 'active_partner' | 'dormant';

// The "Sites" view's own operational lifecycle (companies.site_phase /
// deals.site_phase), independent of deal stage / company kind — see
// GET /api/sites and internal/models.SitePhase* on the backend. Empty
// string means unset. Renamed to real-world wording in
// migrations/00024_site_phase_relabel.sql (old:
// presales/installation/handover/amc/closed).
export type SitePhase = '' | 'contacting' | 'mockup' | 'material_supplied' | 'installed' | 'maintaining' | 'closed';

// companies.site_status / deals.site_status — see the backend's
// validateSiteStatus (internal/api/handlers/crm_helpers.go). Both flags and
// note are optional; flags keys are a fixed set the UI renders as toggles,
// but the server accepts (and preserves) any boolean-valued key.
export interface SiteStatus {
  flags?: Record<string, boolean>;
  note?: string;
}

export const SITE_STATUS_FLAG_LABELS: Record<string, string> = {
  payment_withheld: 'Payment withheld',
  amc_pending: 'AMC pending',
  manpower_issue: 'Manpower issue',
  material_pending: 'Material pending'
};

export interface Company {
  id: number;
  vertical_id: number;
  kind: 'company' | 'hotel' | 'brand' | 'owner';
  name: string;
  city: string;
  address: Record<string, unknown>;
  phone: string;
  email: string;
  website: string;
  owner_user_id: number | null;
  parent_company_id: number | null;
  owner_company_id: number | null;
  brand_company_id: number | null;
  // Present only on kind=hotel rows (see companyJSON) — resolved server-side
  // so the companies list can show a property's brand/owner without an N+1
  // per-row lookup.
  brand_name?: string | null;
  owner_name?: string | null;
  // Present only on kind=owner rows (see listCompanies) — batched direct-or-
  // via-brand site count, the same rule companyRollup uses for one owner.
  site_count?: number;
  partner_status: PartnerStatus | null;
  site_phase: SitePhase;
  site_status: SiteStatus;
  custom: CustomValues;
  created_at: string;
  updated_at: string;
}

// A minimal company reference, as returned inline on another record's
// related sets (owner/brand links, properties).
export interface CompanyRef {
  id: number;
  name: string;
  kind: 'company' | 'hotel' | 'brand' | 'owner';
}

// Everything the company detail view needs, in one round trip (see
// GET /api/companies/:id?include=related).
export interface CompanyRelated {
  owner?: CompanyRef;
  brand?: CompanyRef;
  people: Person[];
  deals: Deal[];
  activities: Activity[];
  issues: Issue[];
  properties?: Company[];
}

export interface ContactCompany {
  id: number;
  name: string;
  city?: string;
  address?: Record<string, unknown> | null;
}

export interface Person {
  company?: ContactCompany | null;
  company_name?: string | null;
  open_deals?: number;
  open_cases?: number;
  no_followup?: boolean;
  owner_user_id: number | null;
  contact_owner_name: string | null;
  relationship_type: string;
  notes: string;
  last_touchpoint?: Activity | null;
  next_followup?: Activity | null;
  // Earliest open plan (overdue first), returned by contact lists and the summary.
  next_plan?: Activity | null;
  id: number;
  vertical_id: number;
  company_id: number | null;
  // Hotels-vertical-only hierarchy links — see models.Person's doc comment
  // (internal/models/crm.go) for the company_id derivation rule. Always
  // present (possibly null) in API responses; other verticals just never
  // populate them.
  hotel_company_id: number | null;
  brand_company_id: number | null;
  owner_company_id: number | null;
  hotel_name: string | null;
  brand_name: string | null;
  owner_name: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  whatsapp: string;
  job_title: string;
  city: string;
  custom: CustomValues;
  created_at: string;
  updated_at: string;
}

export interface Deal {
  job_status?: string;
  public_code?: string | null;
  installer_name?: string | null;
  feedback_confirmed_at?: string | null;
  order_number?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  customer_city?: string | null;
  installed_at?: string | null;
  feedback_submitted_at?: string | null;
  feedback_answer?: 'fine' | 'not_fine' | null;
  feedback_url?: string | null;
  id: number;
  vertical_id: number;
  company_id: number | null;
  person_id: number | null;
  name: string;
  amount: number;
  currency: string;
  stage: string;
  close_date: string | null;
  referrer_person_id: number | null;
  commission_pct: number | null;
  commission_amount: number | null;
  site_phase: SitePhase;
  site_status: SiteStatus;
  custom: CustomValues;
  created_at: string;
  updated_at: string;
}

// `email` activities are auto-created by the backend IMAP sync engine
// (Phase C) — never created through the activities form. The linked
// email_messages metadata rides along on `email` (see activityJSON in
// internal/api/handlers/activities.go) — s3_key itself is never exposed,
// only the derived has_raw boolean.
export type ActivityType = 'note' | 'task' | 'cold_call' | 'call' | 'meeting' | 'email' | 'whatsapp' | 'visit';

export interface EmailMeta {
  id: number;
  subject: string;
  from_addr: string;
  to_addrs: string;
  cc_addrs: string;
  sent_at: string | null;
  snippet: string;
  // Groups messages in the same conversation (empty when the sync engine
  // couldn't determine one) — drives the touchpoints list's thread grouping.
  thread_key: string;
  has_raw: boolean;
  // 'admin' | 'vertical' — mirrors models.EmailVisibility*. A member never
  // receives an 'admin' row in the first place (see scopeEmailVisibility on
  // the backend); this is purely presentational (Team inbox's "Management
  // only" badge).
  visibility: string;
}

// Free retrospective log entries default to 'completed' on create; a
// meeting/call logged with a future scheduled_at defaults to 'scheduled'
// instead — see the backend's createActivity default-status logic.
export type ActivityStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show';

export interface Activity {
  outcome_tags?: string[];
  no_followup?: boolean;
  occurred_at?: string | null;
  person_name?: string;
  company_name?: string;
  assignee_name?: string | null;
  contact_owner_user_id?: number | null;
  booked_by_name?: string | null;
  touchpoint_at?: string;
  assignee_user_id?: number | null;
  location?: string;
  id: number;
  vertical_id: number;
  type: ActivityType;
  company_id: number | null;
  person_id: number | null;
  deal_id: number | null;
  title: string;
  body: string;
  outcome: string;
  status: string;
  due_at: string | null;
  scheduled_at: string | null;
  next_action: string;
  next_action_date: string | null;
  custom: CustomValues;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Present only on `email`-type activities.
  email?: EmailMeta;
  // Present only on rows returned by the owner rollup (GET
  // /api/companies/:id/rollup) — which of the owner's several sites this
  // touchpoint belongs to, resolved server-side (see companyRollup on the
  // backend) so the owner dashboard's timeline can attribute/link each entry
  // without a second fetch. Absent (undefined) everywhere else, including
  // the single-site dashboard, where it would just point at the page itself.
  site_id?: number;
  site_name?: string;
}

export type IssueStatus = 'open' | 'in_progress' | 'resolved';

export interface Issue {
  assignment_version?: number;
  order_number?: string | null;
  resolving_technician_name?: string | null;
  resolved_at?: string | null;
  last_customer_contact_at?: string | null;
  customer_followup_at?: string | null;
  last_team_update_at?: string | null;
  site_type?: '' | 'hotel' | 'home' | 'office' | 'commercial' | 'other';
  location?: string;
  customer_satisfaction?: '' | 'pending' | 'satisfied' | 'not_satisfied';
  category: 'issue' | 'customer_feedback' | 'customer_log';
  next_action: string;
  next_action_date: string | null;
  id: number;
  vertical_id: number;
  company_id: number | null;
  // Resolved server-side from company_id (see issueJSON on the backend) so
  // the company still shows even when it sorts past the companies-lookup
  // picker's 200-row cap — null when company_id is null, or when the
  // referenced company has been soft-deleted.
  company_name: string | null;
  person_id: number | null;
  title: string;
  body: string;
  unit: string;
  room: string;
  status: IssueStatus;
  assignee_user_id: number | null;
  // Set when the issue came from an external intake (e.g. the WizHom Pro
  // mobile app's support-ticket endpoint) rather than a CRM user's manual
  // report — reporter_* have no corresponding internal person/user record.
  reporter_name: string;
  reporter_phone: string;
  reporter_email: string;
  source: string;
  // CAPA (Corrective and Preventive Action): all three must be non-empty
  // before category 'issue' can transition into status 'resolved' — see the
  // resolve gate in updateIssue on the backend. May be set/edited at any
  // status.
  capa_root_cause: string;
  capa_corrective_action: string;
  capa_preventive_action: string;
  custom: CustomValues;
  // Set together once POST /api/issues/:id/send-to-dev succeeds; null until
  // then. genba_ticket_id non-null both gates re-send (409 server-side) and
  // drives the "Sent to dev" badge.
  genba_ticket_id: number | null;
  sent_to_dev_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Present only on rows returned by the owner rollup (GET
  // /api/companies/:id/rollup) — same site attribution as Activity.site_id/
  // site_name above (an alias of company_id/company_name there, since an
  // issue's company_id already IS the site within that rollup).
  site_id?: number;
  site_name?: string;
}

// GET /api/sites — "Sites" view aggregate rows. Hotels vertical: a site IS a
// kind=hotel company (see listHotelSites on the backend); every other
// vertical (lighting, etc): a site IS a deal (see listDealSites). The two
// shapes never mix within one response — SitesPage picks which to render
// based on the selected vertical's slug, same rule that decides which the
// backend returns.
export interface SiteDealRef {
  id: number;
  name: string;
  stage: string;
  amount: number;
}

export interface HotelSite {
  id: number;
  name: string;
  brand_name: string | null;
  owner_name: string | null;
  site_phase: SitePhase;
  site_status: SiteStatus;
  open_issues: number;
  deals: SiteDealRef[];
}

export interface DealSite {
  feedback_confirmed_at?: string | null;
  order_number?: string | null;
  installed_at?: string | null;
  feedback_submitted_at?: string | null;
  id: number;
  name: string;
  company_id: number | null;
  company_name: string | null;
  stage: string;
  site_phase: SitePhase;
  site_status: SiteStatus;
  open_issues: number;
}

// GET /api/companies/:id/rollup — the Hotels "owner" company-detail rollup:
// every site (HotelSite row) belonging to the owner, direct or via a brand
// it owns, plus the union of open issues and recent activities across those
// sites and rollup-wide aggregates. See companyRollup on the backend.
export interface OwnerRollup {
  owner: CompanyRef;
  sites: HotelSite[];
  aggregates: {
    site_count: number;
    open_issues: number;
    deal_count: number;
    deal_value: number;
  };
  issues: Issue[];
  activities: Activity[];
}

export const ISSUE_COMMENT_KIND_STATUS_CHANGE = 'status_change';
export const ISSUE_COMMENT_KIND_COMMENT = 'comment';

export interface IssueComment {
  id: number;
  issue_id: number;
  user_id: number | null;
  kind: 'comment' | 'status_change';
  body: string;
  created_at: string;
}

// A freeform comment against a lead or company (see GET/POST /api/comments).
// Distinct from IssueComment, which is issue-specific.
export interface Comment {
  id: number;
  entity_type: 'lead' | 'company';
  entity_id: number;
  user_id: number | null;
  author_name: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface IssueAttachment {
  id: number;
  issue_id: number;
  uploader_user_id: number | null;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  // Authenticated file URL (GET /api/attachments/:id/file) — never usable
  // directly in an <img src>; fetch as a blob (see fetchAttachmentBlobUrl).
  url: string;
}

// A file uploaded against a deal card — the bytes live in S3 (see
// internal/api/handlers/deal_attachments.go); download_url is an
// authenticated endpoint that 302s to a short-lived presigned S3 URL, never
// a direct/public link.
export interface DealAttachment {
  id: number;
  deal_id: number;
  file_name: string;
  content_type: string;
  size_bytes: number;
  user_id: number | null;
  uploader_name: string;
  created_at: string;
  updated_at: string;
  download_url: string;
}

// From GET /api/companies/lookup — the issues-only-safe company read (also
// used to drive the "unit required for hotels" client-side rule on the issue
// form) and CompanyCombobox's live search. city/vertical_id are here (unlike
// quickCreateCompany's bare id/name/kind response) so the combobox can show
// a disambiguating city and — for a caller searching across more than one
// vertical — infer which vertical the picked company belongs to.
export interface CompanyLookup {
  id: number;
  name: string;
  kind: 'company' | 'hotel' | 'brand' | 'owner';
  city: string;
  vertical_id: number;
  // Present only on kind=hotel rows (see companyLookupJSON) — lets a Hotels
  // people-form hotel picker auto-fill the brand/group fields.
  brand_company_id?: number | null;
  owner_company_id?: number | null;
  brand_name?: string | null;
  owner_name?: string | null;
}

// From GET /api/users/assignable — deliberately just id+name.
export interface AssignableUser {
  vertical_ids?: number[];
  id: number;
  name: string;
}

export interface AdminUser {
  intake_coordinator?: boolean;
  issue_vertical_ids?: number[];
  id: number;
  email: string;
  name: string;
  role: Role;
  issues_only: boolean;
  dispatch_only: boolean;
  email_access: boolean;
  vertical_id: number | null;
  verticals: number[];
  lead_vertical_ids: number[];
  support_tickets: boolean;
  totp_enabled: boolean;
  last_active: string | null;
  created_at: string;
  temp_password?: string;
  // Mailbox status (Admin > Email access) — additive, see
  // handlers.emailAccountsByUserID. '' status/address means no connected
  // mailbox; never includes credentials. These three fields reflect only ONE
  // representative account (the most recently connected) — email_account_count
  // is the honest total when a user has connected more than one (see
  // handlers.userJSON); Admin > Emails > All inboxes is where every account
  // is actually listed.
  email_account_status: '' | 'connected' | 'error';
  email_account_address: string;
  email_account_last_synced_at: string | null;
  email_account_count: number;
}

// One user's connected mailbox as seen by the admin "manage everyone's
// inboxes" surface (GET/POST /api/admin/email-accounts, PUT/DELETE/test/sync
// by :id — internal/api/handlers/admin_email_accounts.go). Same shape as
// EmailAccount plus the owning user's identity.
export interface AdminEmailAccount extends EmailAccount {
  user_id: number;
  user_name: string;
  user_email: string;
}

export interface Stats {
  stats: Record<string, { companies: number; people: number; deals: number; activities: number }>;
}

export interface LoginEvent {
  id: number;
  user_id: number;
  created_at: string;
  ip_address: string;
  device: string;
  browser: string;
  os: string;
  // Only present on the global recent-logins feed, not the per-user one.
  email?: string;
}

export type LeadStatus = 'download' | 'new' | 'contacted' | 'converted' | 'discarded';
export type LeadNotifyStatus = 'pending' | 'sent' | 'failed' | 'skipped';

export interface Lead {
  issue_id?: number | null;
  purpose: 'enquiry' | 'guide_download';
  guide_id: string;
  guide_audience: string;
  source: string;
  contact_requested: boolean;
  id: number;
  vertical_id: number | null;
  category: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  page: string;
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  raw_json: string;
  ip_address: string;
  spam: boolean;
  status: LeadStatus;
  notify_status: LeadNotifyStatus;
  notify_error: string;
  notified_at: string | null;
  person_id: number | null;
  company_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface LeadRoute {
  id: number;
  category: string;
  vertical_id: number | null;
  notify_emails: string[];
  extra_vertical_ids: number[];
}

export interface LeadSettings {
  id: number;
  default_vertical_id: number | null;
  default_notify_emails: string[];
  global_fallback_email: string[];
  support_ticket_notify_emails: string[];
}

// One of the logged-in user's connected mailboxes (see GET/POST
// /api/me/email-accounts and PUT/DELETE/test/sync by :id) — a user may
// connect more than one; passwords never round-trip here, only
// connection/status metadata.
export interface EmailAccount {
  id: number;
  email_address: string;
  imap_host: string;
  imap_port: number;
  imap_username: string;
  imap_use_ssl: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_use_ssl: boolean;
  status: 'connected' | 'error' | '';
  last_error: string;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProtocolCheckResult {
  ok: boolean;
  error?: string;
}

export interface EmailAccountTestResult {
  imap: ProtocolCheckResult;
  smtp: ProtocolCheckResult;
}

// Admin > Tracked Domains (see /api/admin/tracked-domains). company_name/
// vertical_name/created_by_name/email_count are server-joined — see
// adminTrackedDomainRow / handlers.adminListTrackedDomains's usage-count
// metric doc.
export interface TrackedDomain {
  id: number;
  domain: string;
  company_id: number | null;
  company_name: string | null;
  vertical_id: number;
  vertical_name: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  email_count: number;
}

// From GET /api/me/tracked-domains — the member-facing read (no usage count/
// creator identity, own accessible verticals only). See handlers.
// meListTrackedDomains.
export interface MeTrackedDomain {
  domain: string;
  company_id: number | null;
  company_name: string | null;
  vertical_id: number;
  vertical_name: string;
}

// One counterparty's rollup in the unmatched-email review queue (see
// GET /api/me/email-review — internal/api/handlers/email_review.go's
// reviewGroup).
export interface EmailReviewGroup {
  addr: string;
  name: string;
  domain: string;
  in_count: number;
  out_count: number;
  total: number;
  latest_subject: string;
  latest_sent_at: string | null;
  first_seen: string;
  last_seen: string;
}

// One queued/running/finished backfill request — internal/emailsync.
// BackfillState, echoed by both the 202 from POST /api/me/email-backfill
// ("status") and GET /api/me/email-backfill/status's "running"/"queued".
export interface BackfillState {
  status: 'pending' | 'running' | 'done' | 'error';
  counterparty: string;
  logged: number;
  scanned: number;
  detail?: string;
  started_at?: string;
  finished_at?: string | null;
}

// GET /api/me/email-backfill/status: the account's FIFO backfill queue —
// the currently-running item (null if idle) plus everything still pending
// behind it, in order. See handlers.emailBackfillStatusFor.
export interface BackfillStatus {
  running: BackfillState | null;
  queued: BackfillState[];
}

// GET /api/email-messages/:id/body — the full parsed body of a stored raw
// email (see handlers.emailMessageBody). html is the raw text/html part,
// rendered client-side inside a sandboxed (no-script) iframe.
export interface EmailMessageBody {
  text: string;
  html: string;
  truncated: boolean;
  subject: string;
  from: string;
  to: string;
  cc: string;
  sent_at: string | null;
}

// Spreadsheet import wizard (see internal/api/handlers/import.go /
// internal/importer). ImportMappingTarget mirrors the fixed importer.Target*
// constants; a custom field column is instead referenced dynamically as
// "custom:company:<key>" / "custom:people:<key>" — see ImportWizard.svelte.
export type ImportMappingTarget =
  | 'skip'
  | 'company_name'
  | 'company_city'
  | 'company_pincode'
  | 'company_phone'
  | 'company_email'
  | 'company_website'
  | 'person_name'
  | 'person_email'
  | 'person_phone'
  | 'person_city'
  | 'person_job_title'
  | string; // custom:company:<key> / custom:people:<key>

export interface ImportPreview {
  token: string;
  vertical_id: number;
  headers: string[];
  preview_rows: string[][];
  row_count: number;
  suggested_mapping: ImportMappingTarget[];
}

export interface ImportRowResult {
  row: number;
  company: string; // created | matched | matched (row N) | error
  company_id?: number;
  company_name?: string;
  person: string; // created | matched | matched (row N) | skipped | error
  person_id?: number;
  person_name?: string;
  warnings: string[];
  error?: string;
}

export interface ImportTotals {
  rows: number;
  companies_created: number;
  companies_matched: number;
  people_created: number;
  people_matched: number;
  people_skipped: number;
  errors: number;
}

export interface ImportCommitResult {
  vertical_id: number;
  results: ImportRowResult[];
  totals: ImportTotals;
}

// Plaintext API keys are returned separately, once, when created.
export interface AdminApiKey {
  id: number;
  name: string;
  prefix: string;
  scope: string;
  created_by_user_id: number;
  created_at: string;
  last_used_at: string | null;
  last_used_ip: string | null;
  revoked_at: string | null;
}
